/**
 * Sprite fallback utilities for default standing action IDs
 * After action-emotion merge: sprites use action_ids (e.g., 2016 = standing-joy female, 2042 = standing male)
 */
import { CDN_URL } from '../config.js';
import { normalizeGender } from './genderUtils.js';

export const buildSmallSpriteUrl = (characterId, actionId, outfitId = 0) =>
  `${CDN_URL}/s/small/${parseInt(characterId, 10)}_${outfitId}_${actionId}.webp`;

export const toFullSizeSpriteUrl = (url) => {
  if (typeof url !== 'string' || !url) return url;
  return url.replace(
    /\/s\/small\/(\d+)_(\d+)_(\d+)\.webp\b/,
    '/s/sprites/$1/$2/$3.webp'
  );
};

// Default standing action IDs by gender
const FEMALE_DEFAULT_STANDING = 2016; // standing-joy
const MALE_DEFAULT_STANDING = 2042;   // standing

// Standing action ID pools by gender (used for variety in card grids / rankings)
const FEMALE_STANDING_ACTION_IDS = [2000, 2007, 2008, 2013, 2016];
const MALE_STANDING_ACTION_IDS   = [2030, 2033, 2035, 2042, 2043];

// Legacy sprite state values that should resolve to default standing
const LEGACY_JOY_VALUES = new Set([
    'joy',
    'standing-joy',
    'base_sprites/joy',
    'base_sprites_joy',
    'base_sprites'
]);

/**
 * Get the default standing action ID for a given gender
 * @param {string|number|null|undefined} gender - Gender value (string 'male'/'female', number 0/1, or null)
 * @returns {number} Action ID (2042 for male, 2016 otherwise)
 */
export const getDefaultStandingActionId = (gender) => {
    return normalizeGender(gender) === 'male' ? MALE_DEFAULT_STANDING : FEMALE_DEFAULT_STANDING;
};

/**
 * Check if an action value is a legacy joy/base_sprites string
 * @param {string|number|null|undefined} action
 * @returns {boolean}
 */
export const isLegacyJoyValue = (action) => {
    if (action === null || action === undefined) return false;
    if (typeof action === 'number') return false;
    return LEGACY_JOY_VALUES.has(String(action).toLowerCase());
};

/**
 * Resolve legacy sprite action values to action IDs
 * - Numeric action IDs pass through unchanged
 * - null/undefined, legacy 'joy' strings, and unknown names → gender standing default
 *
 * @param {string|number|null|undefined} action - The action value to resolve
 * @param {string|number|null|undefined} gender - Gender for default ID selection (optional)
 * @returns {number} Resolved action ID
 */
export const resolveLegacySpriteAction = (action, gender = null) => {
    if (action === null || action === undefined || action === '') {
        return getDefaultStandingActionId(gender);
    }

    const numericAction = typeof action === 'number' ? action : parseInt(action, 10);
    if (!Number.isNaN(numericAction) && String(numericAction) === String(action).trim()) {
        return numericAction;
    }

    return getDefaultStandingActionId(gender);
};

/**
 * Pick a standing action ID from the gender-appropriate pool.
 * @param {string|number|null|undefined} gender
 * @param {string|number|null} [seed=null] - When non-null, picks deterministically (stable per character).
 * @returns {number}
 */
export const pickStandingActionId = (gender, seed = null) => {
    const arr = normalizeGender(gender) === 'male'
        ? MALE_STANDING_ACTION_IDS
        : FEMALE_STANDING_ACTION_IDS;
    if (seed !== null && seed !== undefined) {
        return arr[Math.abs(parseInt(String(seed), 10) || 0) % arr.length];
    }
    return arr[Math.floor(Math.random() * arr.length)];
};

export { FEMALE_DEFAULT_STANDING, MALE_DEFAULT_STANDING, FEMALE_STANDING_ACTION_IDS, MALE_STANDING_ACTION_IDS };
